﻿import React, { useState, useEffect } from "react";
import { Box, TextField, InputAdornment } from "@mui/material";
import {
  ShepDataGridPro,
  ShepCheckbox,
  ShepGridWarning,
  ShepPagination,
  ShepHorizontalMenu,
} from "@/components/GenericComponents/ShepComponents";
import axios from "@/features/axiosHelper";
import { createAlert } from "@/features/homepage/alertSliceList";
import { useDispatch } from "react-redux";
import { GridActionsCellItem } from "@mui/x-data-grid";
import {
  setAllConnections,
  setAllCustomFieldsAndValues,
} from "@/features/maintenance/maintenanceSlice";
import { SearchIcon } from "@/ImageRepo";
import AddEditCustomFieldValueForConnection from "./AddEditCustomFieldValueForConnection";

const ConnectionCustomFieldManager = ({}) => {
  const dispatch = useDispatch();

  const [fields, setFields] = useState([]);
  const [searchFilter, setSearchFilter] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [addEditPopupOpen, setAddEditPopupOpen] = useState(false);
  const [recordToEdit, setRecordToEdit] = useState(null);

  useEffect(() => {
    getConnections();
    getFieldsForConnection();
  }, []);

  const getConnections = () => {
    axios
      .get("/connectioncustomfield/connection-dropdown-values", {})
      .then((response) => {
        if (response.data) {
          //setConnections(response.data);
          dispatch(
            setAllConnections({
              allConnections: response.data,
            }),
          );
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response?.data || "Failed to fetch connections",
          }),
        );
      });
  };

  const getFieldsForConnection = () => {
    axios
      .get("/connectioncustomfield/connection-custom-fields")
      .then((response) => {
        if (response.data) {
          setFields(response.data);
          dispatch(
            setAllCustomFieldsAndValues({
              allCustomFieldsAndValues: response.data,
            }),
          );
        }
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message: error.response?.data || "Error fetching data",
          }),
        );
      });
  };

  const handleTextChange = (event) => setSearchFilter(event.target.value);

  const filteredFields = fields.filter((field) => {
    if (!searchFilter) return true;
    const searchLower = searchFilter.toLowerCase();
    // Check against Connection Name and Field Code
    const matchName = field.ConnectionName?.toLowerCase().includes(searchLower);
    const matchCode = field.FieldCode?.toLowerCase().includes(searchLower);

    return matchName || matchCode;
  });

  const validateDelete = () => {
    if (selectedRowIds.length === 0) {
      dispatch(
        createAlert({
          status: "Warning",
          alertType: "single",
          message: "No records selected to delete",
        }),
      );
      return;
    }

    const warningMessage = `Are you sure you want to delete the selected record${
      selectedRowIds.length > 1 ? "s" : ""
    }?`;

    dispatch(
      createAlert({
        status: "Warning",
        alertType: "result",
        title: "Delete Record",
        message: warningMessage,
        buttonLabel: "Delete",
        onButtonClick: () => handleDelete(),
        buttonLabel2: "Cancel",
      }),
    );
  };

  //To be changed
  const handleDelete = () => {
    const groupedData = selectedRowIds.reduce((accumulator, compositeId) => {
      const matchedRow = fields.find(
        (f) => `${f.ConnectionId}-${f.FieldId}` === compositeId,
      );

      if (matchedRow) {
        const { ConnectionId, FieldId } = matchedRow;

        if (!accumulator[ConnectionId]) {
          accumulator[ConnectionId] = [];
        }
        accumulator[ConnectionId].push(FieldId);
      }
      return accumulator;
    }, {});

    axios
      .delete(`/connectioncustomfield/custom-field-values`, {
        data: groupedData,
      })
      .then((response) => {
        dispatch(
          createAlert({
            status: "Success",
            alertType: "single",
            message: response.data,
          }),
        );

        getFieldsForConnection();
        setSelectedRowIds([]);
      })
      .catch((error) => {
        dispatch(
          createAlert({
            status: "Error",
            alertType: "single",
            message:
              error.response?.data || "Failed to delete custom field values.",
          }),
        );
      });
  };

  const actions = [
    {
      label: "Add",
      onClick: () => {
        setRecordToEdit(null);
        setAddEditPopupOpen(true);
      },
    },
    {
      label: "Delete",
      onClick: () => validateDelete(),
      variant: "danger",
    },
  ];

  const columns = [
    { field: "ConnectionName", headerName: "Connection Name", width: 350 },
    { field: "FieldCode", headerName: "Field Code", width: 350 },
    { field: "FieldValue", headerName: "Field Value", flex: 1 },
    {
      field: "threedotMenu",
      type: "actions",
      getActions: (params) => [
        <GridActionsCellItem
          label="Edit"
          showInMenu
          onClick={() => {
            setRecordToEdit(params.row);
            setAddEditPopupOpen(true);
          }}
        />,
      ],
    },
  ];

  return (
    <>
      <Box sx={{ display: "block", flexDirection: "column", width: "100%" }}>
        <Box
          className="content-box"
          sx={{ display: "flex", flexDirection: "row" }}
        >
          <Box sx={{ width: "100%" }}>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                paddingBottom: "25px",
              }}
            >
              <TextField
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon
                        style={{ color: "var(--text-icon-regular)" }}
                      />
                    </InputAdornment>
                  ),
                }}
                placeholder="Search by Connection Name or Field Code"
                value={searchFilter}
                onChange={handleTextChange}
                sx={{
                  width: "450px",
                  "&.MuiFormControl-root .MuiInputBase-root": {
                    borderRadius: "5px",
                  },
                }}
              />

              <ShepHorizontalMenu actions={actions} />
            </div>

            <AddEditCustomFieldValueForConnection
              open={addEditPopupOpen}
              onClose={() => {
                setAddEditPopupOpen(false);
                setRecordToEdit(null);
              }}
              onSuccess={() => {
                setAddEditPopupOpen(false);
                setRecordToEdit(null);
                getFieldsForConnection(); // Refresh grid after add/edit
              }}
              recordToEdit={recordToEdit}
            />

            <Box sx={{ width: "100%", maxWidth: "100%", overflowX: "auto" }}>
              {filteredFields.length > 0 ? (
                <ShepDataGridPro
                  isDataLoaded={true}
                  rows={filteredFields}
                  columns={columns}
                  columnHeaderHeight={78}
                  rowHeight={100}
                  whiteHeader={true}
                  checkboxSelection={true}
                  disableMultipleRowSelection={false}
                  disableColumnMenu={true}
                  disableColumnFilter={true}
                  disableColumnReorder={true}
                  hideFooterSelectedRowCount={true}
                  pagination
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 15, page: 0 },
                    },
                  }}
                  slots={{
                    baseCheckbox: ShepCheckbox,
                    pagination: ShepPagination,
                  }}
                  pageSizeOptions={[6]}
                  onRowSelectionModelChange={(newRowSelectionModel) => {
                    setSelectedRowIds(newRowSelectionModel);
                  }}
                  // Composite Key: Crucial for master list uniqueness!
                  getRowId={(row) => `${row.ConnectionId}-${row.FieldId}`}
                  sx={{
                    color: "var(--text-primary)",
                    fontSize: "var(--font-sm)",
                    fontFamily: "var(--font-family)",
                    borderTopLeftRadius: "12px",
                    borderTopRightRadius: "12px",
                    backgroundColor: "var(--default-light)",
                    border: "none",
                    "& .MuiDataGrid:hover": {
                      backgroundColor: "inherit",
                    },
                    "& .MuiDataGrid-cellCheckbox, & .workflow-row": {
                      opacity: 0,
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-cell": {
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-row:hover": {
                      backgroundColor: "var(--tertiary-13)",
                    },
                    "& .Mui-selected": {
                      backgroundColor: "var(--primary-pale)",
                    },
                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within, & .MuiDataGrid-columnHeader:focus":
                      {
                        outline: "none",
                      },
                    "& .MuiDataGrid-row:hover .MuiDataGrid-cellCheckbox, & .MuiDataGrid-row:hover .workflow-row":
                      {
                        opacity: 1,
                      },
                    "& .Mui-selected .MuiDataGrid-cellCheckbox": {
                      opacity: 1,
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontSize: "var(--font-md)",
                      fontWeight: "var(--font-bold)",
                      borderLeft: "none",
                    },
                    "& .MuiDataGrid-columnHeaders, & .MuiDataGrid-columnHeader:hover":
                      {
                        borderBottomLeftRadius: "4px",
                        borderBottomRightRadius: "4px",
                        backgroundColor: "var(--primary-pale)",
                        borderBottom: "none",
                        borderLeft: "none",
                      },
                  }}
                />
              ) : (
                <ShepGridWarning
                  title="No fields assigned."
                  message="Click 'Add' to assign a custom field and value to a connection."
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ConnectionCustomFieldManager;
